import { useCallback } from 'react';

import { graphql, useFragment, useMutation } from 'react-relay';

import { useAnalyticsEvents } from '../../../../common/analytics/generated/use-analytics-events';
import { DEFAULT_PAGE_SIZE } from '../../../../common/constants';
import type { NetworkStorageParams } from '../../../../common/types';
import { useOptions } from '../../../options-provider';
import type {
	useNavSettingsService$data,
	useNavSettingsService$key,
} from './__generated__/useNavSettingsService.graphql';
import type {
	useNavSettingsServiceMutation,
	useNavSettingsServiceMutation$variables,
} from './__generated__/useNavSettingsServiceMutation.graphql';

export type UseNavSettingsServiceParams = Omit<NetworkStorageParams, 'entityAri' | 'queryRef'> & {
	mutationEntityAri: string;
	queryRef: useNavSettingsService$key | null | undefined;
};

export function useNavSettingsService(params: UseNavSettingsServiceParams): {
	queryData: useNavSettingsService$data | null | undefined;
	setNavData: ({
		sidebar,
		properties,
	}: Pick<useNavSettingsServiceMutation$variables, 'sidebar' | 'properties'>) => void;
} {
	const { mutationEntityAri: entityAri, ownerAri, queryRef } = params;
	const queryData = useLocalFragment(queryRef);
	const mutation = useLocalMutation();
	const { fireEvent } = useAnalyticsEvents();
	const { customizationStore } = useOptions();

	const fireMutationEvent = useCallback(
		(success: boolean, error?: string) => {
			switch (success) {
				case true:
					fireEvent('operational.executeMutation.succeeded', {
						storage: customizationStore ?? 'network',
					});
					break;
				case false:
					fireEvent('operational.executeMutation.failed', {
						storage: customizationStore ?? 'network',
						error: error ?? 'Unknown error',
					});
					break;
			}
		},
		[customizationStore, fireEvent],
	);

	const setNavData = useCallback(
		({
			sidebar,
			properties,
		}: Pick<useNavSettingsServiceMutation$variables, 'sidebar' | 'properties'>): void => {
			executeMutation({
				mutation,
				variables: {
					entityAri,
					ownerAri,
					properties,
					sidebar,
					pageSizeValue: DEFAULT_PAGE_SIZE,
				},
				fireEvent: fireMutationEvent,
			});
		},
		[entityAri, fireMutationEvent, mutation, ownerAri],
	);

	return { queryData, setNavData };
}

// NOTE: `sidebar` and `properties` below use a literal `first: 100` rather than a
// Relay provider. The value MUST stay in sync with `DEFAULT_PAGE_SIZE` and with the
// page size GlobalTopNavigation uses when reading `properties` on this same record
// (see `useSideNavToggleStateQuery` in @atlassian/global-top-navigation).
//
// A provider cannot be used here. GlobalRoot composes this fragment into its own
// query, and Relay emits the provider as a path-relative import in the *composing*
// package's generated artifact — so @atlassian/global-root would import a module
// from this package's `src`, which is not part of its published dist. Relay also
// identifies providers by that relative path string, so the same provider cannot be
// shared across packages to make the `properties` arguments merge.
function useLocalFragment(queryRef: UseNavSettingsServiceParams['queryRef']) {
	/* eslint-disable @atlassian/relay/no-wrapping-hooks */
	/* eslint-disable @atlassian/relay/unused-fields */
	/* eslint-disable @atlassian/relay/graphql-naming */
	return useFragment<useNavSettingsService$key>(
		graphql`
			fragment useNavSettingsService on SettingsNavigationCustomisation {
				sidebar(first: 100) {
					nodes {
						menuId
						visible
					}
				}
				properties(first: 100) {
					nodes {
						key
						value
					}
				}
			}
		`,
		queryRef,
	);
	/* eslint-enable @atlassian/relay/unused-fields */
}

function useLocalMutation() {
	return useMutation<useNavSettingsServiceMutation>(graphql`
		mutation useNavSettingsServiceMutation(
			$entityAri: ID!
			$ownerAri: ID!
			$sidebar: [SettingsMenuItemInput]
			$properties: [SettingsDisplayPropertyInput]
			$pageSizeValue: Int!
		) {
			settings_updateNavigationCustomisation(
				input: {
					entityAri: $entityAri
					ownerAri: $ownerAri
					properties: $properties
					sidebar: $sidebar
				}
			) {
				sidebar(first: $pageSizeValue) {
					nodes {
						menuId
						visible
					}
				}
				properties(first: $pageSizeValue) {
					nodes {
						key
						value
					}
				}
			}
		}
	`)[0];
}

type ExecuteMutationParams = {
	fireEvent: (success: boolean, error?: string) => void;
	mutation: ReturnType<typeof useLocalMutation>;
	variables: useNavSettingsServiceMutation$variables;
};

function executeMutation({ mutation, variables, fireEvent }: ExecuteMutationParams) {
	try {
		return mutation({
			variables: variables,
			onCompleted: () => {
				fireEvent(true);
			},
			onError: (error: Error) => {
				fireEvent(false, error.message || 'Unknown error');
			},
		});
	} catch (error: unknown) {
		fireEvent(false, `Unhandled error: ${(error as Error).message || 'Unknown error'}`);
	}
}
